import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  IngredientChoice,
  IngredientChoicesType,
  OrderableItem,
  OrderableItemService,
  OrderedItem,
  OrderedItemService,
  IngredientChoiceGroup,
} from 'ngx-web-api';
import { distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { ItemEditorService } from '../../core/services/item-editor.service';
import { SpecialWizardEditingItemChoicesService } from '../../core/services/special-wizard-editing-item-choices.service';
import { HighlightElementsService } from '../highlight-elements.service';

@Component({
  selector: 'fts-special-wizard-modal-footer',
  templateUrl: './special-wizard-modal-footer.component.html',
  styleUrls: ['./special-wizard-modal-footer.component.scss'],
})
export class SpecialWizardModalFooterComponent implements OnChanges, OnDestroy, AfterViewInit {
  @Input()
  orderableItem: OrderableItem;
  @Input()
  orderedItem: OrderedItem;
  @Input()
  isLastStep: boolean;
  @Input()
  stepOrdinal: number;
  @Input()
  specialAdditionFailedMode: boolean;
  @Input()
  shouldForceAddToOrder: boolean;
  @Input()
  addingSpecialToOrder: Subscription;
  @Output()
  nextStepClick: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  addToOrderClick: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  cancelClick: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  resetSpecialAdditionFailedMode: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('footer', { static: true })
  private footer: ElementRef;

  showStepsButtons: boolean;
  instructionsVisible: boolean;
  isFirstChoiceVisible: boolean;
  nextChoice?: IngredientChoice;
  nextChoiceGroup?: IngredientChoiceGroup;
  visibleChoice?: IngredientChoice;
  visibleChoiceGroup: IngredientChoiceGroup;
  footerTopPosition = 0;
  showBackBtn: boolean;
  showCancelBtn: boolean;
  showNextBtn: boolean;
  showDoneBtn: boolean;
  disableNextButton: boolean;

  private choiceSubscription: Subscription;
  private orderedChoiceSubscription: Subscription;
  private choiceGroupSubscription: Subscription;

  constructor(
    public orderedItemService: OrderedItemService,
    private orderableItemService: OrderableItemService,
    private itemEditorService: ItemEditorService,
    private specialWizardEditingItemChoices: SpecialWizardEditingItemChoicesService,
    private cdr: ChangeDetectorRef,
    private highlightService: HighlightElementsService
  ) {
    this.choiceSubscription = this.specialWizardEditingItemChoices.visibleChoice$
      .pipe(distinctUntilChanged())
      .subscribe((choice: IngredientChoice) => {
        this.visibleChoice = choice;
        if (choice && !!this.orderableItem) {
          const visibleOrderedChoice = this.orderedItem.getChoice(choice.name);
          this.specialWizardEditingItemChoices.setVisibleOrderedChoice(visibleOrderedChoice);
          this.isFirstChoiceVisible =
            this.identifyVisibleChoices()
              .map(ingredientChoice => ingredientChoice.name)
              .indexOf(choice.name) === 0;
          this.calculateButtonStatus();
        }
      });

    this.orderedChoiceSubscription = this.specialWizardEditingItemChoices.visibleOrderedChoice$.subscribe(choice => {
      this.nextChoice = null;

      if (!!this.orderableItem) {
        const choices = this.identifyVisibleChoices();
        this.nextChoice = this.specialWizardEditingItemChoices.getNextChoice(choices);
        this.calculateNextChoiceGroup(choices);
      }
      this.calculateButtonStatus();
    });

    this.choiceGroupSubscription = this.specialWizardEditingItemChoices.visibleChoiceGroup$.subscribe(choiceGroup => {
      this.visibleChoiceGroup = choiceGroup;
      this.calculateButtonStatus();
    });

    this.itemEditorService.ingredientChanged$.subscribe(() => {
      this.calculateButtonStatus();
    });
  }

  ngAfterViewInit(): void {
    this.calculateFooterTopPosition();
  }

  @HostListener('window:resize')
  onResize() {
    this.calculateFooterTopPosition();
  }

  calculateFooterTopPosition() {
    this.footerTopPosition = window.innerHeight - this.footer.nativeElement.clientHeight;
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['orderableItem']) {
      this.instructionsVisible = this.orderableItemService.shouldAskForInstructions(this.orderableItem);
      this.showStepsButtons =
        !!this.orderableItem && this.itemEditorService.getChoicesLayout(this.orderableItem) === IngredientChoicesType.Steps;
      this.calculateButtonStatus();
    }

    if (changes['orderedItem'] || changes['stepOrdinal'] || changes['specialAdditionFailedMode'] || changes['shouldForceAddToOrder']) {
      this.calculateButtonStatus();
    }
    setTimeout(() => this.calculateFooterTopPosition());
  }

  ngOnDestroy(): void {
    this.choiceSubscription.unsubscribe();
    this.orderedChoiceSubscription.unsubscribe();
    this.choiceGroupSubscription.unsubscribe();
  }

  cancelClicked() {
    this.cancelClick.next();
  }

  goToNextChoice() {
    if (this.disableNextButton) {
      this.highlightService.highLightAll(true);
      return;
    }
    if (this.orderableItem.choiceGroups && this.orderableItem.choiceGroups.length) {
      const filteredChoices = this.identifyVisibleChoices();
      const filteredGroups = this.itemEditorService.filterChoiceGroupsByDependency(
        this.orderableItem.choiceGroups,
        this.orderableItem.choices,
        this.orderedItem
      );
      const nextChoiceGroup = this.specialWizardEditingItemChoices.getNextChoiceGroup(filteredGroups);

      if (nextChoiceGroup) {
        this.specialWizardEditingItemChoices.setVisibleChoiceGroup(nextChoiceGroup);
        const firstChoice = filteredChoices.find(
          ch =>
            ch.name ===
            nextChoiceGroup.choices.filter(choice => !!filteredChoices.find(filterredChoice => filterredChoice.name === choice))[0]
        );
        this.specialWizardEditingItemChoices.setVisibleChoice(firstChoice);
      }
    } else if (this.nextChoice) {
      this.specialWizardEditingItemChoices.setVisibleChoice(this.nextChoice);
    }
  }

  backBtnClicked() {
    if (this.specialAdditionFailedMode) {
      this.resetSpecialAdditionFailedMode.emit();
    } else {
      this.goToPreviousChoice();
    }
  }

  private identifyVisibleChoices() {
    if (this.orderableItem && this.orderedItem) {
      return this.rearangeDependants(this.orderedItemService.getFilteredIngredientChoices(this.orderableItem, this.orderedItem)).filter(
        choice => !choice.isInstructions
      );
    }
  }

  private rearangeDependants(choices: IngredientChoice[]): IngredientChoice[] {
    return [...choices.filter(c => !c.dependsOn), ...choices.filter(c => c.dependsOn)];
  }

  private calculateNextChoiceGroup(filteredChoices: IngredientChoice[]) {
    if (this.orderableItem.choiceGroups && this.orderableItem.choiceGroups.length) {
      const filteredGroups = this.itemEditorService.filterChoiceGroupsByDependency(
        this.orderableItem.choiceGroups,
        this.orderableItem.choices,
        this.orderedItem
      );
      this.nextChoiceGroup = this.specialWizardEditingItemChoices.getNextChoiceGroup(filteredGroups);
    }
  }

  private goToPreviousChoice() {
    if (this.orderableItem.choiceGroups && this.orderableItem.choiceGroups.length) {
      const filteredChoices = this.identifyVisibleChoices();
      const filteredGroups = this.itemEditorService.filterChoiceGroupsByDependency(
        this.orderableItem.choiceGroups,
        this.orderableItem.choices,
        this.orderedItem
      );
      const previousChoiceGroup = this.specialWizardEditingItemChoices.getPreviousChoiceGroup(filteredGroups);

      if (previousChoiceGroup) {
        this.specialWizardEditingItemChoices.setVisibleChoiceGroup(previousChoiceGroup);
        const firstChoice = filteredChoices.find(
          ch =>
            ch.name ===
            previousChoiceGroup.choices.filter(choice => !!filteredChoices.find(filterredChoice => filterredChoice.name === choice))[0]
        );
        this.specialWizardEditingItemChoices.setVisibleChoice(firstChoice);
      }
    } else if (this.visibleChoice && !this.isFirstChoiceVisible) {
      const choices = this.identifyVisibleChoices();
      const choiceIndex = choices.map(ingredientChoice => ingredientChoice.name).indexOf(this.visibleChoice.name) - 1;
      const previousChoice = choices[choiceIndex];
      this.specialWizardEditingItemChoices.setVisibleChoice(previousChoice);
    }
  }

  private shouldShowBackBtn(): boolean {
    if (this.specialAdditionFailedMode) {
      return true;
    }

    if (this.shouldForceAddToOrder || !this.showStepsButtons) {
      return false;
    }

    if (this.itemHasChoiceGroups()) {
      return this.visibleChoiceGroup && this.visibleChoiceGroup.name !== this.orderableItem.choiceGroups[0].name;
    }

    if (!this.isFirstChoiceVisible && !!this.visibleChoice && !!this.orderableItem) {
      return this.orderedItemService.getFilteredIngredientChoices(this.orderableItem, this.orderedItem).length > 0;
    }

    return false;
  }

  private shouldShowCancelBtn(): boolean {
    return !this.shouldForceAddToOrder && !this.specialAdditionFailedMode && !this.showStepsButtons;
  }

  private shouldShowNextBtn(): boolean {
    if (this.shouldForceAddToOrder || this.specialAdditionFailedMode || !this.showStepsButtons) {
      return false;
    }

    if (this.itemHasChoiceGroups()) {
      return !!this.nextChoiceGroup;
    }

    return this.hasNextChoice();
  }

  private shouldShowDoneBtn(): boolean {
    if (this.specialAdditionFailedMode) {
      return false;
    }

    if (this.shouldForceAddToOrder) {
      return true;
    }

    if (!this.orderableItem || !this.orderedItem || !this.orderedItemService.isValid(this.orderedItem, this.orderableItem)) {
      return false;
    }

    if (!this.showStepsButtons) {
      return true;
    }

    if (this.itemHasChoiceGroups()) {
      return !this.nextChoiceGroup;
    }
    return !this.hasNextChoice();
  }

  private itemHasChoiceGroups(): boolean {
    return !!this.orderableItem && !!this.orderableItem.choiceGroups && this.orderableItem.choiceGroups.length > 0;
  }

  private hasNextChoice(): boolean {
    return !!this.nextChoice && this.orderedItemService.getFilteredIngredientChoices(this.orderableItem, this.orderedItem).length > 0;
  }

  private shouldDisableNextButton(): boolean {
    return this.itemHasChoiceGroups()
      ? this.visibleChoiceGroup && this.orderedItem && this.orderedItem.hasInvalidChoiceFor(this.visibleChoiceGroup)
      : this.orderedItem && this.visibleChoice && !this.orderedItem.getChoice(this.visibleChoice.name).isValid();
  }

  private calculateButtonStatus() {
    this.showBackBtn = this.shouldShowBackBtn();
    this.showCancelBtn = this.shouldShowCancelBtn();
    this.showNextBtn = this.shouldShowNextBtn();
    this.showDoneBtn = this.shouldShowDoneBtn();
    this.disableNextButton = this.shouldDisableNextButton();
  }
}
